import { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantLeads, getTenantContractors } from '../../services/crmService'; 

// Free CDN link for the US Map topology
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const stateLabels = [
  { name: "Alabama", code: "AL", coordinates: [-86.9023, 32.3182] },
  { name: "Alaska", code: "AK", coordinates: [-152.4044, 61.3707] },
  { name: "Arizona", code: "AZ", coordinates: [-111.0937, 34.0489] },
  { name: "Arkansas", code: "AR", coordinates: [-92.1999, 35.2010] },
  { name: "California", code: "CA", coordinates: [-119.4179, 36.7783] },
  { name: "Colorado", code: "CO", coordinates: [-105.7821, 39.5501] },
  { name: "Connecticut", code: "CT", coordinates: [-73.0877, 41.6032] },
  { name: "Delaware", code: "DE", coordinates: [-75.5277, 38.9108] },
  { name: "Florida", code: "FL", coordinates: [-81.5158, 27.6648] },
  { name: "Georgia", code: "GA", coordinates: [-83.4326, 32.1574] },
  { name: "Hawaii", code: "HI", coordinates: [-157.4983, 21.2894] },
  { name: "Idaho", code: "ID", coordinates: [-114.7420, 44.0682] },
  { name: "Illinois", code: "IL", coordinates: [-89.3985, 40.6331] },
  { name: "Indiana", code: "IN", coordinates: [-86.1269, 40.5512] },
  { name: "Iowa", code: "IA", coordinates: [-93.0977, 41.8780] },
  { name: "Kansas", code: "KS", coordinates: [-98.4842, 39.0119] },
  { name: "Kentucky", code: "KY", coordinates: [-84.2700, 37.8393] },
  { name: "Louisiana", code: "LA", coordinates: [-91.9623, 31.2448] },
  { name: "Maine", code: "ME", coordinates: [-69.4455, 45.2538] },
  { name: "Maryland", code: "MD", coordinates: [-76.6413, 39.0458] },
  { name: "Massachusetts", code: "MA", coordinates: [-71.3824, 42.4072] },
  { name: "Michigan", code: "MI", coordinates: [-85.6024, 44.3148] },
  { name: "Minnesota", code: "MN", coordinates: [-94.6859, 46.7296] },
  { name: "Mississippi", code: "MS", coordinates: [-89.3985, 32.3547] },
  { name: "Missouri", code: "MO", coordinates: [-92.2884, 37.9643] },
  { name: "Montana", code: "MT", coordinates: [-110.3626, 46.9219] },
  { name: "Nebraska", code: "NE", coordinates: [-99.9018, 41.4925] },
  { name: "Nevada", code: "NV", coordinates: [-116.4194, 38.8026] },
  { name: "New Hampshire", code: "NH", coordinates: [-71.5639, 43.1939] },
  { name: "New Jersey", code: "NJ", coordinates: [-74.4057, 40.0583] },
  { name: "New Mexico", code: "NM", coordinates: [-106.2485, 34.5199] },
  { name: "New York", code: "NY", coordinates: [-74.2179, 43.2994] },
  { name: "North Carolina", code: "NC", coordinates: [-79.0193, 35.7596] },
  { name: "North Dakota", code: "ND", coordinates: [-100.4370, 47.5515] },
  { name: "Ohio", code: "OH", coordinates: [-82.9071, 40.4173] },
  { name: "Oklahoma", code: "OK", coordinates: [-97.5164, 35.0078] },
  { name: "Oregon", code: "OR", coordinates: [-120.5542, 43.8041] },
  { name: "Pennsylvania", code: "PA", coordinates: [-77.1945, 41.2033] },
  { name: "Rhode Island", code: "RI", coordinates: [-71.4774, 41.5801] },
  { name: "South Carolina", code: "SC", coordinates: [-81.1637, 33.8361] },
  { name: "South Dakota", code: "SD", coordinates: [-99.9018, 44.2998] },
  { name: "Tennessee", code: "TN", coordinates: [-86.5804, 35.5175] },
  { name: "Texas", code: "TX", coordinates: [-99.9018, 31.9686] },
  { name: "Utah", code: "UT", coordinates: [-111.0937, 39.3210] },
  { name: "Vermont", code: "VT", coordinates: [-72.5778, 44.5588] },
  { name: "Virginia", code: "VA", coordinates: [-78.6569, 37.4316] },
  { name: "Washington", code: "WA", coordinates: [-120.7401, 47.7511] },
  { name: "West Virginia", code: "WV", coordinates: [-80.4549, 38.5976] },
  { name: "Wisconsin", code: "WI", coordinates: [-89.6165, 44.5003] },
  { name: "Wyoming", code: "WY", coordinates: [-107.2903, 43.0760] }
];

const getStateFullName = (abbr) => {
  const stateObj = stateLabels.find(s => s.code === abbr.toUpperCase());
  return stateObj ? stateObj.name : abbr;
};

export default function GodModeMap() {
  const { tenantId, loading: tenantLoading } = useTenant(); 

  const [rotateX, setRotateX] = useState(45); 
  const [rotateZ, setRotateZ] = useState(-15); 
  const [activeState, setActiveState] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  
  const [liveStateData, setLiveStateData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTelemetryMapData = async () => {
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        const deals = await getTenantLeads(tenantId);
        const contractors = await getTenantContractors(tenantId);

        const repLocations = {};
        contractors.forEach(c => {
          if (c.operatingCity && c.operatingState) {
            repLocations[c.id] = `${c.operatingCity}, ${c.operatingState}`;
          } else {
            repLocations[c.id] = c.location || 'Unknown, UNK';
          }
        });

        const aggregatedData = {};

        deals.forEach(deal => {
          // ENTERPRISE FIX: Capture every penny. Summing MRR + One-Time Implementation + Direct Paid
          const mrr = Number(deal.mrr_value || deal.mrr || 0);
          const setup = Number(deal.calculatedOneTime || deal.setupFee || deal.amountPaid || 0);
          const totalDealValue = mrr + setup;
          
          if (totalDealValue <= 0) return; // Skip zero-value leads

          const isWebLead = !deal.rep_uid;
          const repUid = deal.rep_uid || 'default_web_lead';
          const repName = deal.rep_name || 'Matt Reinhardt';
          
          let locationString = repLocations[repUid];
          
          // Default all unassigned web volume to Washington
          if (isWebLead || !locationString) {
            locationString = 'Oak Harbor, WA'; 
          }

          const parts = locationString.split(',');
          const city = parts[0]?.trim() || 'Unknown City';
          const stateAbbr = parts[1]?.trim() || 'UNK';
          const fullStateName = getStateFullName(stateAbbr);

          if (!aggregatedData[fullStateName]) {
            aggregatedData[fullStateName] = {
              totalValue: 0,
              reps: new Set(),
              citiesMap: {},
              color: "#10B981" 
            };
          }

          aggregatedData[fullStateName].totalValue += totalDealValue;
          aggregatedData[fullStateName].reps.add(repName.split(' ')[0] + ' ' + (repName.split(' ')[1]?.[0] || ''));

          if (!aggregatedData[fullStateName].citiesMap[city]) {
            aggregatedData[fullStateName].citiesMap[city] = 0;
          }
          aggregatedData[fullStateName].citiesMap[city] += totalDealValue;
        });

        const finalStateData = {};
        Object.keys(aggregatedData).forEach(stateKey => {
          const data = aggregatedData[stateKey];
          
          const sortedCities = Object.keys(data.citiesMap)
            .map(cityName => ({ name: cityName, valNum: data.citiesMap[cityName] }))
            .sort((a, b) => b.valNum - a.valNum)
            .map(c => ({ name: c.name, val: `$${c.valNum.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}` }));

          finalStateData[stateKey] = {
            revenue: `$${data.totalValue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`,
            topReps: Array.from(data.reps).slice(0, 3), 
            color: data.totalValue > 15000 ? "#22D3EE" : "#10B981", 
            cities: sortedCities
          };
        });

        setLiveStateData(finalStateData);

      } catch (error) {
        console.error("Failed to load map telemetry:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTelemetryMapData();
  }, [tenantId, tenantLoading]);

  const handleStateClick = (stateName) => {
    if (selectedState === stateName) {
      setSelectedState(null);
    } else {
      setSelectedState(stateName);
    }
  };

  const displayState = selectedState || activeState;
  const currentData = displayState ? liveStateData[displayState] : null;

  return (
    <div className="animate-in fade-in duration-700 bg-[#070B14] border border-slate-800 shadow-2xl relative flex flex-col h-[650px] mb-8">
      
      <div className="p-4 border-b border-slate-800 bg-[#0B1120] flex justify-between items-center z-20 relative shadow-md">
        <div>
          <h3 className="text-xs font-black text-amber-500 uppercase tracking-[0.2em] flex items-center gap-2">
            <i className="fa-solid fa-crown"></i> Founder Mode: Kinetic Map
          </h3>
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">
            {isLoading || tenantLoading ? "SYNCING SATELLITE DATA..." : (selectedState ? `LOCKED: ${selectedState.toUpperCase()} - CLICK AGAIN TO UNLOCK` : "CLICK STATE TO LOCK DATA")}
          </p>
        </div>
        
        <div className="flex gap-6 items-center bg-[#070B14] p-2 border border-slate-800 shadow-inner">
          <div className="flex items-center gap-3">
            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Tilt</span>
            <input 
              type="range" min="0" max="70" value={rotateX} 
              onChange={(e) => setRotateX(e.target.value)}
              className="w-24 accent-amber-500 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Spin</span>
            <input 
              type="range" min="-90" max="90" value={rotateZ} 
              onChange={(e) => setRotateZ(e.target.value)}
              className="w-24 accent-cyan-500 cursor-pointer"
            />
          </div>
          <button 
            onClick={() => { setRotateX(45); setRotateZ(-15); setSelectedState(null); }}
            className="text-[9px] font-bold text-slate-400 hover:text-white uppercase tracking-widest ml-2 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex-1 relative perspective-[1200px] flex items-center justify-center bg-[#070B14] overflow-hidden">
        
        {(isLoading || tenantLoading) && (
          <div className="absolute z-50 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[10px] font-bold text-cyan-500 uppercase tracking-widest animate-pulse">Decrypting Geo-Data...</p>
          </div>
        )}

        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, #1E293B 1px, transparent 1px), linear-gradient(to bottom, #1E293B 1px, transparent 1px)',
          backgroundSize: '40px 40px', opacity: 0.15
        }}></div>

        <div 
          className="w-full max-w-5xl transition-transform duration-100 ease-out preserve-3d flex items-center justify-center"
          style={{ transform: `rotateX(${rotateX}deg) rotateZ(${rotateZ}deg) translateY(-5%) scale(1.1)` }}
        >
          <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;
                  const isHeat = liveStateData[stateName];
                  const isSelected = selectedState === stateName;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setActiveState(stateName)}
                      onMouseLeave={() => setActiveState(null)}
                      onClick={() => handleStateClick(stateName)}
                      style={{
                        default: {
                          fill: isSelected ? "#1E293B" : (isHeat ? isHeat.color : "#0F172A"),
                          fillOpacity: isSelected ? 1 : (isHeat ? 0.6 : 0.8),
                          stroke: isSelected ? "#22D3EE" : "#000000", 
                          strokeWidth: isSelected ? 2.5 : 1.5,
                          outline: "none",
                          transition: "all 0.3s"
                        },
                        hover: {
                          fill: isHeat ? isHeat.color : "#1E293B",
                          fillOpacity: 1,
                          stroke: "#22D3EE",
                          strokeWidth: 2,
                          outline: "none",
                          cursor: "pointer"
                        },
                        pressed: {
                          fill: "#10B981",
                          outline: "none"
                        }
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {stateLabels.map(({ name, code, coordinates }) => (
              <Marker key={code} coordinates={coordinates}>
                <text
                  textAnchor="middle"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "10px",
                    fontWeight: "900",
                    fill: "#334155", 
                    pointerEvents: "none",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    opacity: 0.8
                  }}
                >
                  {code}
                </text>
              </Marker>
            ))}
          </ComposableMap>
        </div>
      </div>

      {displayState && !(isLoading || tenantLoading) && (
        <div className="absolute bottom-6 left-6 bg-[#0B1120] border border-slate-700 p-4 shadow-2xl z-30 min-w-[250px] animate-in slide-in-from-bottom-4">
          <h4 className="text-xs font-black text-white uppercase tracking-widest border-b border-slate-800 pb-2 mb-3 flex justify-between items-center">
            <span>{displayState} Sector</span>
            {selectedState === displayState && (
              <span className="text-[8px] text-cyan-400 bg-cyan-900/30 px-1.5 py-0.5"><i className="fa-solid fa-lock"></i></span>
            )}
          </h4>
          
          {currentData ? (
            <div className="space-y-4">
              <div>
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">Sector Revenue (MRR + Setup)</p>
                <p className="text-xl font-black text-emerald-400">{currentData.revenue}</p>
              </div>
              
              {selectedState === displayState ? (
                <div>
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2 border-b border-slate-800 pb-1">Top Performing Hubs</p>
                  <ul className="text-xs font-bold text-slate-300 space-y-2">
                    {currentData.cities.map((city, idx) => (
                      <li key={idx} className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-cyan-500 rounded-none"></span> {city.name}
                        </span>
                        <span className="text-emerald-500">{city.val}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Top 1099 Operators</p>
                  <ul className="text-xs font-bold text-slate-300 space-y-1">
                    {currentData.topReps.map((rep, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-cyan-500 rounded-full"></span> {rep}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[8px] text-slate-500 mt-2 italic">Click state to view city data</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs font-bold text-slate-500 py-2">No active telemetry data for this region.</p>
          )}
        </div>
      )}
    </div>
  );
}